import { useState } from 'react';
import type { CostRow } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { useCosts } from './useCosts';
import { downloadCostsCsv } from './exportCsv';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { Label as FormLabel } from '../../components/ui/Label';
import type { CostGroupBy } from '../../lib/api-client';
import { providerIdPt } from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';

/** Current calendar month as `YYYY-MM`, the default filter value — a fresh page load should show "this month's spend" without the user having to pick anything first. Uses the browser's local clock/zone on purpose (unlike `UsagePage.tsx`'s `formatPeriod`, which parses a UTC value the API already returned): there's no server value to stay pinned to here, this is just the sensible default for an `<input type="month">` the user is about to interact with. */
function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function totals(rows: CostRow[]): { labelCount: number; totalPriceCents: number } {
  return rows.reduce(
    (acc, row) => ({
      labelCount: acc.labelCount + row.label_count,
      totalPriceCents: acc.totalPriceCents + row.total_price_cents,
    }),
    { labelCount: 0, totalPriceCents: 0 },
  );
}

const GROUP_BY_OPTIONS: { value: CostGroupBy | ''; label: string }[] = [
  { value: '', label: 'Provedor × transportadora (sem agregação)' },
  { value: 'provider', label: 'Provedor' },
  { value: 'carrier', label: 'Transportadora' },
];

/**
 * `/costs` (TTP-250, TRV-19) — consolidated spend by provider/carrier/month,
 * read straight from `GET /v1/costs` (already aggregated server-side, see
 * `useCosts.ts`). The totals row and the CSV export both work off the same
 * `rows` array the table renders — never a separate fetch/recomputation
 * (CLAUDE.md/issue: "nunca somar client-side sem paginação completa" — this
 * endpoint has no pagination to begin with, so the array in hand IS the
 * complete month/tenant answer).
 */
export function CostsPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [period, setPeriod] = useState(currentPeriod());
  const [groupBy, setGroupBy] = useState<CostGroupBy | ''>('');

  const { rows, loading, error } = useCosts(apiKey, period, groupBy);
  const { labelCount, totalPriceCents } = totals(rows);

  function handleExport() {
    downloadCostsCsv(rows, `custos-${period}${groupBy ? `-${groupBy}` : ''}.csv`);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Custos</h1>
        <p className="text-sm text-muted-foreground">
          Gasto real com etiquetas emitidas (status pronta), por provedor e transportadora — só tráfego em modo live
          conta aqui.
        </p>
      </div>

      <div className="flex flex-col gap-3 border border-border p-4 sm:flex-row sm:items-end sm:gap-4">
        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="costs-period-filter">Período</FormLabel>
          <input
            id="costs-period-filter"
            type="month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="costs-group-by-filter">Agrupar por</FormLabel>
          <select
            id="costs-group-by-filter"
            value={groupBy}
            onChange={(event) => setGroupBy(event.target.value as CostGroupBy | '')}
            className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {GROUP_BY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <Button type="button" variant="outline" onClick={handleExport} disabled={rows.length === 0}>
          Exportar CSV
        </Button>
      </div>

      {!loading && !error && rows.length > 0 ? (
        <div className="grid grid-cols-1 border-l border-t border-border sm:grid-cols-2">
          <div className="flex flex-col gap-2 border-b border-r border-border p-6">
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Total do período
            </span>
            <span className="text-3xl font-medium text-foreground">{formatCentsToBRL(totalPriceCents)}</span>
          </div>
          <div className="flex flex-col gap-2 border-b border-r border-border p-6">
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Ticket médio
            </span>
            <span className="text-3xl font-medium text-foreground">
              {formatCentsToBRL(labelCount > 0 ? Math.round(totalPriceCents / labelCount) : 0)}
            </span>
          </div>
        </div>
      ) : null}

      {error ? (
        <Alert variant="error" title="Não foi possível carregar os custos">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando custos…" />
      ) : rows.length === 0 ? (
        <Card className="flex flex-col gap-2 p-6">
          <p className="text-sm text-foreground">Nenhum custo neste período.</p>
          <p className="text-sm text-muted-foreground">
            Só etiquetas emitidas com sucesso (status pronta) em modo live entram nesse total — troque o período ou
            emita etiquetas para ver dados aqui.
          </p>
        </Card>
      ) : (
        <>
          <div className="hidden overflow-x-auto border border-border lg:block">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  <th scope="col" className="px-4 py-3">
                    Período
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Provedor
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Transportadora
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Nº de etiquetas
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Valor total
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.provider ?? 'all'}-${row.carrier ?? 'all'}-${index}`} className="border-b border-border/60">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.period}</td>
                    <td className="px-4 py-3">{row.provider ? providerIdPt(row.provider) : '—'}</td>
                    <td className="px-4 py-3">{row.carrier ?? '—'}</td>
                    <td className="px-4 py-3">{row.label_count.toLocaleString('pt-BR')}</td>
                    <td className="px-4 py-3">{formatCentsToBRL(row.total_price_cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-medium text-foreground">
                  <td className="px-4 py-3" colSpan={3}>
                    Total
                  </td>
                  <td className="px-4 py-3">{labelCount.toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3">{formatCentsToBRL(totalPriceCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-col gap-4 lg:hidden">
            {rows.map((row, index) => (
              <Card key={`${row.provider ?? 'all'}-${row.carrier ?? 'all'}-${index}`} className="flex flex-col gap-2 p-4">
                <div className="font-mono text-xs text-muted-foreground">{row.period}</div>
                <div className="text-sm text-foreground">
                  {row.provider ? providerIdPt(row.provider) : 'Todos os provedores'} ·{' '}
                  {row.carrier ?? 'Todas as transportadoras'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {row.label_count.toLocaleString('pt-BR')} etiqueta{row.label_count === 1 ? '' : 's'}
                </div>
                <div className="text-lg font-medium text-foreground">{formatCentsToBRL(row.total_price_cents)}</div>
              </Card>
            ))}
            <Card className="flex flex-col gap-2 p-4">
              <div className="text-sm font-medium text-foreground">Total do período</div>
              <div className="text-sm text-muted-foreground">
                {labelCount.toLocaleString('pt-BR')} etiqueta{labelCount === 1 ? '' : 's'}
              </div>
              <div className="text-lg font-medium text-foreground">{formatCentsToBRL(totalPriceCents)}</div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
